import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { PAGE_ACCESS_CATALOG, type PageAccessKey } from '@floorfile/contracts'
import { useAuth } from './auth'
import { useEventSubscription } from './event-feed'
import { readLocaleSeed, readTimezoneSeed, writeLocaleSeed, writeTimezoneSeed } from './locale-seed'

export interface CompanyNavigationSettings {
  allowedPageKeys: string[]
  showArchivedItems: boolean
  chatEnabled: boolean
  exportsEnabled: boolean
  uploadsEnabled: boolean
  aiEnabled: boolean
  advancedDashboardEnabled: boolean
  defaultLocale: 'en' | 'fr'
  allowUserLocaleOverride: boolean
  anonymousIncidentsEnabled: boolean
  logoUrl: string | null
  timezone: string
  effectiveLocale: 'en' | 'fr'
  /** Owner decision 2026-09-16 (round 2): employees may see their own HR records. */
  hrRecordsSelfView: boolean
  /** Owner decision 2026-09-16 (round 2): a requester may approve their own deviation / conclude their own OOS. */
  deviationsAllowSelfApproval: boolean
  /** FF-QA-071: a raiser may disposition their own NCR (`moduleSettings.ncr.allowSelfDisposition`, default off). Optional so an older server parses. */
  ncrAllowSelfDisposition?: boolean | undefined
  /** FF-QA-084 (owner decision 2026-09-27): this person is in the deviation approver set (owner/admin, named approver, or supervisor when Policies allows). */
  deviationsCanApprove: boolean
  /** FF-QA-084: this person may raise a PLANNED deviation (owner/admin, named submitter, or supervisor when Policies allows). */
  deviationsCanSubmitPlanned: boolean
  /** Fiix audit Phase 3.1: the public work-request portal switch (Settings → Features). */
  workRequestPortalEnabled: boolean
  /** ISO 4217 code for money on screen, null = plain numbers (owner decision 2026-09-18). */
  currency: string | null
  /** Owner decision 2026-09-18: LOCK = a check past due + grace is missed and cannot be started; ALLOW = it stays completable. */
  checksLateCompletion: 'ALLOW' | 'LOCK'
  /** Owner decision 2026-09-30: under LOCK, extra hours a check started in time gets to be finished; null = none. */
  checksStartedExtensionHours: number | null
  /** Owner decision 2026-09-19: this person's role is one the company shows costs to. */
  costsVisible: boolean
  /** F3 (OD-6): this person may read equipment/part change history (Settings → Policies). */
  canViewRecordHistory?: boolean | undefined
  /** F6: PB-1's mode — ASK makes the check runner ask whether the machine still runs. */
  equipmentStateMode?: 'ASK' | 'AUTO' | 'OFF' | undefined
  /** F30 / FF-QA-003: the company's offline-at-rest policy, read here by every
   * role and handed to auth (`applyCompanyPolicy`) — this one request serves
   * both the nav and the force-enrol gate. Optional so an older server parses. */
  requireOfflineEncryption?: boolean | undefined
}

interface CompanyConfigState extends CompanyNavigationSettings {
  status: 'loading' | 'ready' | 'error'
  allowedPages: ReadonlySet<string>
  isPageAllowed: (key: string | undefined) => boolean
  refresh: () => Promise<void>
}

const defaults: CompanyNavigationSettings = {
  allowedPageKeys: PAGE_ACCESS_CATALOG.map((entry) => entry.key),
  showArchivedItems: false,
  chatEnabled: true,
  exportsEnabled: true,
  uploadsEnabled: true,
  aiEnabled: false,
  advancedDashboardEnabled: true,
  defaultLocale: 'en',
  allowUserLocaleOverride: true,
  anonymousIncidentsEnabled: false,
  logoUrl: null,
  timezone: 'UTC',
  effectiveLocale: 'en',
  hrRecordsSelfView: true,
  deviationsAllowSelfApproval: false,
  ncrAllowSelfDisposition: false,
  deviationsCanApprove: false,
  deviationsCanSubmitPlanned: false,
  checksLateCompletion: 'LOCK',
  checksStartedExtensionHours: 8,
  workRequestPortalEnabled: false,
  currency: null,
  costsVisible: false,
  canViewRecordHistory: false,
}

/**
 * The defaults, with the locale seeded from this device's last resolved one
 * (localization plan §3.6).
 *
 * On a cold PWA start with no network `/settings/navigation` fails and the
 * provider stays on `defaults` — which would have flipped a French plant's
 * tablet to English for as long as it had no signal. Only the locale is
 * seeded: every other default here is a permission or capability, and guessing
 * those from a stale device hint would be showing someone a page they may no
 * longer be allowed to open.
 */
function seededDefaults(): CompanyNavigationSettings {
  const seed = readLocaleSeed()
  const timezone = readTimezoneSeed()
  return {
    ...defaults,
    ...(seed ? { defaultLocale: seed, effectiveLocale: seed } : {}),
    // FF-AUDIT-088: the last-known company zone, so an offline reload formats
    // due times as the plant reads them rather than in UTC.
    ...(timezone ? { timezone } : {}),
  }
}

const CompanyConfigContext = createContext<CompanyConfigState | undefined>(undefined)

export function pageKeyForWebPath(pathname: string): PageAccessKey | undefined {
  let match: { key: PageAccessKey; path: string } | undefined
  for (const entry of PAGE_ACCESS_CATALOG) {
    for (const path of entry.webPaths) {
      const matches = path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`)
      if (matches && (!match || path.length > match.path.length)) match = { key: entry.key, path }
    }
  }
  return match?.key
}

export function CompanyConfigProvider({ children }: { children: ReactNode }) {
  const { authFetch, status: authStatus, applyCompanyPolicy } = useAuth()
  const [status, setStatus] = useState<CompanyConfigState['status']>('loading')
  const [settings, setSettings] = useState(seededDefaults)

  const refresh = useCallback(async () => {
    if (authStatus !== 'authenticated') return
    // See modules.tsx — a THROWN authFetch used to escape into `void refresh()`
    // and leave `status` at 'loading' forever, wedging the shell (audit RUN-11).
    try {
      const response = await authFetch('/api/v1/settings/navigation')
      if (!response.ok) { setStatus('error'); return }
      const body = (await response.json()) as { data: CompanyNavigationSettings }
      setSettings(body.data)
      // FF-QA-003: auth used to fetch this same resource for the policy alone.
      applyCompanyPolicy(body.data)
      // Mirror the authoritative answer so the NEXT cold offline start opens in
      // the right language.
      writeLocaleSeed(body.data.effectiveLocale)
      writeTimezoneSeed(body.data.timezone)
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }, [applyCompanyPolicy, authFetch, authStatus])

  useEffect(() => {
    if (authStatus === 'authenticated') void refresh()
    else { setSettings(seededDefaults()); setStatus('loading') }
  }, [authStatus, refresh])
  useEventSubscription(['settings.updated'], () => void refresh())

  const allowedPages = useMemo(() => new Set(settings.allowedPageKeys), [settings.allowedPageKeys])
  const value = useMemo<CompanyConfigState>(() => ({
    ...settings,
    status,
    allowedPages,
    isPageAllowed: (key) => !key || allowedPages.has(key),
    refresh,
  }), [settings, status, allowedPages, refresh])

  return <CompanyConfigContext.Provider value={value}>{children}</CompanyConfigContext.Provider>
}

export function useCompanyConfig(): CompanyConfigState {
  const value = useContext(CompanyConfigContext)
  if (!value) throw new Error('useCompanyConfig must be used inside <CompanyConfigProvider>')
  return value
}
